import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useEffect,
} from "react";
import styles from "./styles.module.scss";
import { Input } from "antd-mobile";
import { useNavigate } from "react-router-dom";
import { getFeesRate, getAbleRentalPeriod } from "@/api/guland";
import DropdownSelect from "./../dropdownSelect";
import MyModal from "@/view/landGame/jewelLand/modal";
import TipModal from "./../tipsModal";
import { getLandMaterialType, getConsumableHoldCount } from "@/view/landGame/utils";
import { Toast } from "antd-mobile";
import _ from "lodash";

const getHoldNum = getConsumableHoldCount;

const SelectInfo = forwardRef(({ selectData = [], type, publishType, isConsumable = false, consumableFilter = {}, refresh }, ref) => {
  const navigate = useNavigate();
  const [price, setPrice] = useState("");
  const [listingQuantity, setListingQuantity] = useState("");
  const [daysOptions, setDaysOptions] = useState([]);
  const [selectList, setSelectList] = useState({});
  const [feesRateData, setFeesRateData] = useState({});
  const [filterData, setFilterData] = useState({});

  const getDaysOptions = async () => {
    try {
      const res = await getAbleRentalPeriod();
      if (res.code !== "000000") throw res.msg;
      const arr = res.data?.split(",");
      const listTemp = [];
      arr.forEach((item) => {
        if (item === '-1') {
          listTemp.push({ label: "永久", value: Number(item) });
        } else {
          listTemp.push({ label: item + "天", value: Number(item) });
        }
      });
      setDaysOptions(listTemp);
    } catch (error) {
      console.log(error);
      Toast.show(error);
    }
  };

  const getFeesRateData = async () => {
    try {
      const res = await getFeesRate();
      if (res.code !== "000000") throw res.msg;
      setFeesRateData(res?.data || {});
    } catch (error) {
      console.log(error);
      Toast.show(error);
    }
  };
  const levelChange = (data) => {
    setFilterData(data);
  };

  useEffect(() => {
    if (!isConsumable && type !== 3) {
      getDaysOptions();
    }
    getFeesRateData();
  }, [isConsumable, type]);

  useEffect(() => {
    const selectDataTemp = {};
    selectData &&
      selectData.forEach((item) => {
        if (!selectDataTemp[item.collectionName]) {
          selectDataTemp[item.collectionName] = [];
        }
        selectDataTemp[item.collectionName].push(item);
      });
    setSelectList(selectDataTemp);
  }, [selectData]);

  // 土地耗材用 feesRate5，其他藏品出租用 feesRate3
  const feesRate = useMemo(() => {
    return Number(isConsumable ? feesRateData.feesRate5 : feesRateData.feesRate3) || 0;
  }, [isConsumable, feesRateData]);

  const rateCount = useMemo(() => {
    const baseAmount = isConsumable
      ? Number(listingQuantity || 0) * Number(price || 0)
      : Number(price || 0);
    const gemCount = (baseAmount * feesRate) / 100;
    if (gemCount >= 1) {
      return Math.floor(gemCount);
    } else if (gemCount > 0) {
      return 1;
    }
    return 0;
  }, [isConsumable, listingQuantity, price, feesRate]);

  const totalFee = useMemo(() => {
    if (isConsumable) {
      return rateCount;
    }
    return selectData?.length * rateCount;
  }, [isConsumable, selectData, rateCount]);

  const count = useMemo(() => {
    if (isConsumable) {
      return Number(listingQuantity || 0) * Number(price || 0);
    }
    return selectData?.length * price;
  }, [isConsumable, selectData, price, listingQuantity]);

  const onConfirm = () => {
    if (selectData?.length === 0) {
      Toast.show(`尚未选择${isConsumable ? '土地耗材' : type === 3 ? '藏品' : type === 1 ? "无聊猿" : "土地"}`);
      return;
    }
    if (isConsumable && !listingQuantity) {
      Toast.show('请输入上架数量');
      return;
    }
    if (isConsumable) {
      const holdNum = getHoldNum(selectData[0]);
      if (Number(listingQuantity) > holdNum) {
        Toast.show('上架数量不能超过持有数量');
        return;
      }
      const landMaterialType = getLandMaterialType(consumableFilter, selectData);
      if (!landMaterialType) {
        Toast.show('请先在分类中选择土地管理券或土地加速卡');
        return;
      }
    }
    if (!price) {
      Toast.show(isConsumable ? '请输入上架价格' : '请输入租赁价格');
      return;
    }
    if (!isConsumable && type !== 3 && !filterData?.value) {
      Toast.show('请选择租期');
      return;
    }
    const landMaterialType = isConsumable
      ? getLandMaterialType(consumableFilter, selectData)
      : undefined;
    const ModalInstance = MyModal.show({
      content: (
        <TipModal
          type={type}
          publishType={publishType ?? type}
          isConsumable={isConsumable}
          selectData={selectData}
          selectList={selectList}
          rentalPeriod={filterData}
          rentalFee={price}
          landMaterialType={landMaterialType}
          count={listingQuantity}
          earning={count - totalFee}
          onClose={(flag) => {
            ModalInstance?.close();
            if (flag) {
              refresh();
            }
          }}
        />
      ),
    });
  };

  useImperativeHandle(ref, () => ({
    refresh: () => {
      setPrice("");
      setListingQuantity("");
      setSelectList({});
      setFilterData({});
      if (!isConsumable && type !== 3) {
        getDaysOptions();
      }
      getFeesRateData();
    },
  }));

  const numberChange = (val, setter) => {
    const reg = /^\+?[1-9][0-9]*$/;
    if (!reg.test(val)) {
      setter("");
    } else {
      setter(val);
    }
  };

  const priceChange = (val) => {
    if (val === price) return;
    numberChange(val, setPrice);
  };

  const quantityChange = (val) => {
    if (val === listingQuantity) return;
    numberChange(val, setListingQuantity);
  };

  const selectedSummary = Object.entries(selectList).map((item) => {
    const name = item[0];
    const itemCount = isConsumable
      ? Number(listingQuantity) || 0
      : item[1]?.length || 0;
    if (isConsumable) {
      return `${itemCount}个${name}`;
    }
    return `${itemCount}个${name}${type === 1 ? "无聊猿" : ""}`;
  }).join('');

  return (
    <div className={styles.info_out}>
      <div className={styles.input_out}>
        {isConsumable ? (
          <div className={styles.input_box}>
            <div className={styles.label}>数量：</div>
            <div className={styles.input}>
              <Input
                value={listingQuantity}
                type="number"
                onChange={quantityChange}
                placeholder="请输入上架数量"
              ></Input>
            </div>
          </div>
        ) : null}
        <div className={styles.input_box}>
          <div className={styles.label}>价格：</div>
          <div className={styles.input}>
            <Input
              value={price}
              type="number"
              onChange={priceChange}
              placeholder={isConsumable ? "请输入上架价格" : "请输入租赁价格"}
            ></Input>
            <div className={styles.icon}></div>
          </div>
        </div>
        {!isConsumable ? (
          <div className={styles.select_box}>
            <div className={styles.label}>租期：</div>
            <div className={styles.select}>
              {type === 3 ? <div className={styles.forever}>永久租赁</div> : (
                <DropdownSelect
                  title=""
                  options={daysOptions}
                  onChange={levelChange}
                />
              )}
            </div>
          </div>
        ) : null}
      </div>

      <div className={styles.checked_box}>
        <div className={styles.checked_info}>{`已选：${selectedSummary}`}</div>
        <div className={styles.checked_info}>{`预计收入：${
          count - totalFee
        }宝石${totalFee === 0 ? '' : `（消耗${totalFee}宝石手续费）`}`}</div>
      </div>

      <div className={styles.btn_box}>
        <div
          className={styles.btn_cancle}
          onClick={() => {
            navigate(-1);
          }}
        ></div>
        <div className={styles.btn_publish} onClick={onConfirm}></div>
      </div>
    </div>
  );
});

export default SelectInfo;
